import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Card, CardTitle, Skeleton } from '@/components/ui';
import { HomeHero } from '@/components/home/HomeHero';
import { SearchBar } from '@/components/search/SearchBar';
import { HomeEventCard } from '@/components/home/HomeEventCard';
import { HomePartnerCard } from '@/components/home/HomePartnerCard';
import { HomeExperienceCard } from '@/components/home/HomeExperienceCard';
import { useSession } from '@/lib/auth';
import { useEvents } from '@/hooks/useEvents';
import { usePartnerProducts } from '@/hooks/usePartnerProducts';
import { useMembershipPlans, type MembershipPlan } from '@/hooks/useMembershipPlans';
import { useExperiences } from '@/hooks/useExperiences';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useMyOrders } from '@/hooks/useMyOrders';
import { formatCOP } from '@/lib/format';
import { firstNameOf } from '@/lib/personName';
import { CategoriaBadge } from '@/components/CategoriaBadge';

/**
 * Home — landing curado de la taquillera (`/`).
 *
 * ROUND9 UAT-C07: el Home dejó de ser el listado de eventos (eso ahora
 * vive en `/eventos`). Es una portada storefront que da overview de todo
 * el catálogo: hero + eventos destacados + tienda + membresías +
 * experiencias, y un mini-resumen personal si hay sesión. Consume los
 * mismos hooks reales que las páginas dedicadas, en modo "destacados".
 *
 * REDISEÑO VISUAL (solo estético): hero inmersivo a sangre con imagen REAL de
 * público en concierto (portada representativa del catálogo) fundida con el
 * fondo; cards con media real (`coverImage` / `imageUrl`) + fallback de marca
 * con textura; banda de usuario compacta; membresías jerarquizadas (plan
 * insignia diferenciado); estados de eventos completos (loading / error /
 * vacío). Cero cambios de datos, hooks, rutas o copy.
 */
export function Home(): JSX.Element {
  const { t } = useTranslation('events');
  const { user } = useSession();
  const events = useEvents();
  const products = usePartnerProducts();
  const plans = useMembershipPlans();
  const experiences = useExperiences();
  const profile = useUserProfile();
  const orders = useMyOrders(!!user);

  const featuredEvents = (events.data ?? []).slice(0, 3);
  const featuredProducts = (products.data ?? []).slice(0, 4);
  // Retención sin render: la sección "Tienda partners" está OCULTA (ver el bloque
  // comentado más abajo). Se referencian los símbolos para conservarlos INTACTOS
  // sin que TS (`noUnusedLocals`) ni ESLint los marquen como muertos. Reactivar la
  // Tienda = descomentar ese bloque (y estas dos líneas de retención se pueden quitar).
  void HomePartnerCard;
  void featuredProducts;
  const featuredExperiences = (experiences.data ?? []).slice(0, 3);
  const tiers = plans.data ?? [];

  // Plan insignia = el de mayor precio mensual (decisión visual, no altera
  // datos ni marca "recomendado" nada que no lo esté en el backend).
  const flagship = tiers.reduce<MembershipPlan | null>(
    (best, tier) => ((tier.monthlyPriceCents ?? 0) > (best?.monthlyPriceCents ?? -1) ? tier : best),
    null,
  );
  const flagshipSlug = flagship?.slug ?? null;

  // Fondo del hero: la portada REAL más representativa del catálogo (la que
  // más se repite entre eventos → la imagen "de la casa", que en el seed real
  // es una escena de público en concierto). Sin inventar assets; degrada a la
  // primera portada disponible y, si no hay ninguna, el hero usa solo gradiente.
  const heroBackdrop = pickHeroBackdrop(events.data ?? []);

  return (
    <div>
      {/* 1 — HERO */}
      <HomeHero heroImage={heroBackdrop} />

      {/* 1b — BUSCADOR PRINCIPAL. Superpuesto sobre el borde del hero (margen
          negativo) para el look de marketplace; en móvil el negativo es menor,
          porque ahí el buscador es una columna más alta. `z-10` para que quede
          por encima del hero. */}
      <div className="container-fc relative z-10 -mt-8 md:-mt-10">
        <SearchBar />
      </div>

      <div className="container-fc flex flex-col gap-14 py-12 md:gap-20 md:py-16">
        {/* 2 — Mini-resumen personal (solo con sesión) */}
        {user && (
          <PersonalStrip
            firstName={firstNameOf(profile.data?.fullName ?? user.email)}
            loyaltyTier={profile.data?.loyaltyTier ?? null}
            orderCount={orders.data?.length ?? null}
          />
        )}

        {/* 3 — EVENTOS DESTACADOS (siempre presente; nunca desaparece en
            silencio: muestra loading / error / vacío según corresponda). */}
        <section>
          <SectionHeader
            title="Eventos destacados"
            subtitle="Los próximos eventos para conseguir tus boletas."
            to="/eventos"
            linkLabel="Ver todos los eventos"
          />
          {events.isPending ? (
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <EventCardSkeleton key={i} />
              ))}
            </div>
          ) : events.isError ? (
            <StateNote role="alert">{t('list.error')}</StateNote>
          ) : featuredEvents.length === 0 ? (
            <StateNote>{t('list.empty')}</StateNote>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {featuredEvents.map((evt) => (
                <HomeEventCard key={evt.id} event={evt} />
              ))}
            </div>
          )}
        </section>

        {/* 4 — TIENDA partners — OCULTA temporalmente (deshabilitación de la Tienda,
            pedido del Operator 2026-08-21). El código queda RETENIDO INTACTO: el import
            `HomePartnerCard`, el hook `usePartnerProducts` y `featuredProducts` siguen
            vivos (por eso los eslint-disable arriba); sólo se retira del DOM. Para
            reactivar la Tienda: descomentar este bloque.

        {featuredProducts.length > 0 && (
          <section>
            <SectionHeader
              title="Tienda partners"
              subtitle="Productos y servicios curados por los partners First Class."
              to="/store"
              linkLabel="Ver tienda"
            />
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {featuredProducts.map((p) => (
                <HomePartnerCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        )}
        */}

        {/* 5 — MEMBRESÍAS */}
        {tiers.length > 0 && (
          <section>
            <SectionHeader
              title="Membresías"
              subtitle="Elegí el plan que mejor acompaña tu forma de vivir First Class."
              to="/memberships"
              linkLabel="Ver planes"
            />
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
              {tiers.map((tier) => (
                <MembershipTeaser
                  key={tier.slug}
                  tier={tier}
                  flagship={tier.slug === flagshipSlug}
                />
              ))}
            </div>
          </section>
        )}

        {/* 6 — EXPERIENCIAS */}
        {featuredExperiences.length > 0 && (
          <section>
            <SectionHeader
              title="Experiencias VIP"
              subtitle="Cenas privadas, viajes curados y encuentros de alto nivel."
              to="/experiences"
              linkLabel="Ver todas"
            />
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
              {featuredExperiences.map((exp) => (
                <HomeExperienceCard key={exp.id} experience={exp} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

/**
 * Elige la imagen de fondo del hero entre las portadas REALES de los eventos:
 * la que más se repite (la imagen "de la casa" del catálogo). Si todas son
 * únicas, cae en la primera; si no hay portadas, devuelve null (hero a
 * gradiente). No inventa ni descarga nada — solo elige entre datos existentes.
 */
function pickHeroBackdrop(events: ReadonlyArray<{ coverImage: string | null }>): string | null {
  const counts = new Map<string, number>();
  for (const e of events) {
    if (e.coverImage) counts.set(e.coverImage, (counts.get(e.coverImage) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = 0;
  for (const [url, n] of counts) {
    if (n > bestCount) {
      best = url;
      bestCount = n;
    }
  }
  return best;
}

/** Nota de estado (error / vacío) con el lenguaje de superficie del sistema. */
function StateNote({ children, role }: { children: React.ReactNode; role?: 'alert' }): JSX.Element {
  return (
    <Card variant="outlined" padded className="mt-6">
      <p role={role} className="text-sm text-fc-muted">
        {children}
      </p>
    </Card>
  );
}

/** Skeleton que replica la silueta de `HomeEventCard` durante la carga. */
function EventCardSkeleton(): JSX.Element {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-card border border-fc-line bg-fc-paper shadow-sm">
      <Skeleton shape="aspect-[16/10] w-full rounded-none" srLabel="Cargando eventos…" />
      <div className="flex flex-1 flex-col gap-2 p-4 md:p-5">
        <Skeleton shape="h-5 w-4/5" srLabel="" />
        <Skeleton shape="h-4 w-2/3" srLabel="" />
        <div className="mt-4 flex items-center justify-between border-t border-fc-line/70 pt-4">
          <Skeleton shape="h-5 w-24" srLabel="" />
          <Skeleton shape="h-5 w-20" srLabel="" />
        </div>
      </div>
    </div>
  );
}

function SectionHeader({
  title,
  subtitle,
  to,
  linkLabel,
}: {
  title: string;
  subtitle: string;
  to: string;
  linkLabel: string;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <div className="flex items-center gap-3">
          <span aria-hidden className="h-6 w-1.5 flex-none rounded-full bg-primary" />
          <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">{title}</h2>
        </div>
        <p className="mt-1.5 pl-[18px] text-sm text-fc-muted">{subtitle}</p>
      </div>
      <Link
        to={to}
        className="group inline-flex items-center gap-1 rounded-card px-1 text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        {linkLabel}
        <span aria-hidden className="transition-transform duration-200 group-hover:translate-x-0.5">
          →
        </span>
      </Link>
    </div>
  );
}

function PersonalStrip({
  firstName,
  loyaltyTier,
  orderCount,
}: {
  firstName: string | null;
  loyaltyTier: string | null;
  orderCount: number | null;
}): JSX.Element {
  const initial = (firstName ?? 'FC').trim().charAt(0).toUpperCase() || 'F';
  return (
    <Card variant="default" padded className="!p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <span
            aria-hidden
            className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-full bg-primary-50 font-display text-lg font-semibold text-primary"
          >
            {initial}
          </span>
          <div className="min-w-0">
            {/* ROUND17 #1 + #2: saludo con primer nombre + Categoría FC pill. */}
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate font-display text-base font-semibold text-fc-ink">
                {firstName ? `Hola, ${firstName}` : 'Tu cuenta First Class'}
              </p>
              {loyaltyTier && <CategoriaBadge value={loyaltyTier} />}
            </div>
            <p className="mt-0.5 truncate text-sm text-fc-muted">
              {orderCount != null && orderCount > 0
                ? `Tenés ${orderCount} ${orderCount === 1 ? 'compra' : 'compras'} en tu historial.`
                : 'Todavía no tenés compras — explorá el catálogo y conseguí tu primera boleta.'}
            </p>
          </div>
        </div>
        <div className="flex flex-none flex-wrap gap-2">
          <Button as={Link} to="/my-purchases" variant="secondary" size="sm">
            Mis compras
          </Button>
          <Button as={Link} to="/account" variant="ghost" size="sm">
            Mi cuenta
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** Acento superior por tono de plan (barra fina que sangra a los bordes). */
const TIER_ACCENT: Record<MembershipPlan['tone'], string> = {
  neutral: 'bg-neutral-300',
  gold: 'bg-fc-gold',
  success: 'bg-success',
  info: 'bg-accent',
};

function MembershipTeaser({
  tier,
  flagship,
}: {
  tier: MembershipPlan;
  flagship: boolean;
}): JSX.Element {
  const priceLabel =
    tier.monthlyPriceCents === null
      ? 'A medida'
      : tier.monthlyPriceCents === 0
        ? 'Gratis'
        : `${formatCOP(tier.monthlyPriceCents)} / mes`;

  // Plan insignia — superficie premium oscura para diferenciarlo del resto.
  if (flagship) {
    return (
      <Card
        variant="default"
        padded
        className="flex h-full flex-col border-primary-700 bg-gradient-to-br from-neutral-900 to-primary-900 text-white shadow-lg transition duration-200 hover:-translate-y-1 hover:shadow-xl"
      >
        <div className="flex items-start justify-between gap-2">
          <span className="inline-flex items-center rounded-full bg-white/10 px-2.5 py-0.5 text-xs font-medium text-white ring-1 ring-inset ring-white/20">
            {tier.name}
          </span>
          {tier.recommended && (
            <span className="inline-flex items-center rounded-full bg-cyan/20 px-2.5 py-0.5 text-xs font-medium text-cyan">
              Recomendado
            </span>
          )}
        </div>
        <h3 className="mt-3 font-display text-2xl font-bold">{priceLabel}</h3>
        <p className="mt-2 text-sm text-white/70">{tier.tagline}</p>
        <div className="mt-auto pt-5">
          <Link
            to={`/memberships/${tier.slug}`}
            className="inline-flex h-9 w-full items-center justify-center rounded-card bg-white px-4 text-sm font-semibold text-primary-700 transition-colors hover:bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            Ver detalle
          </Link>
        </div>
      </Card>
    );
  }

  return (
    <Card
      variant="default"
      padded
      className="flex h-full flex-col overflow-hidden transition duration-200 hover:-translate-y-1 hover:border-primary-200 hover:shadow-lg"
    >
      <span
        aria-hidden
        className={`-mx-4 -mt-4 mb-4 h-1.5 md:-mx-6 md:-mt-6 ${TIER_ACCENT[tier.tone]}`}
      />
      <div className="flex items-start justify-between gap-2">
        <Badge tone={tier.tone}>{tier.name}</Badge>
        {tier.recommended && <Badge tone="gold">Recomendado</Badge>}
      </div>
      <CardTitle className="mt-3 text-2xl">{priceLabel}</CardTitle>
      <p className="mt-2 text-sm text-fc-muted">{tier.tagline}</p>
      <div className="mt-auto pt-5">
        <Button
          as={Link}
          to={`/memberships/${tier.slug}`}
          variant="secondary"
          size="sm"
          className="w-full justify-center"
        >
          Ver detalle
        </Button>
      </div>
    </Card>
  );
}
